// The vendor's billing in Payouts (aldoo-na store/payouts/billing), like an ads platform's billing pages:
//   Commission  — what Aldoo's commission on cash on delivery sales comes to beyond the online payouts: due
//                 at the next settlement, owed now (Pay now: an early payment), the charges to the card
//   Documents   — Aldoo's invoices for its commission: one per settlement (a payout, or a card charge)
import React, { useEffect, useMemo, useRef, useState } from "react"
import PDFViewer from "aldoo-ra/PDFViewer"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import usePdfModal from "./usePdfModal"
import Spinner from "aldoo-ra/Spinner"
import { BuildingLibraryIcon } from "@heroicons/react/24/outline"
import { CheckCircleIcon, ExclamationCircleIcon } from "@heroicons/react/24/solid"
import { showErrorToast } from "aldoo-ra/Toast"
import { Chip, Empty, Section, HelpTip, money, dateOnly } from "../AdminShared/ui"
import { BillingInvoiceFile, BillingPayNow, BillingManual, BillingManualCheck, BillingProformaFile } from "../AdminShared/api"
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import { getStripePromise } from "aldoo-ra/Store/stripe-client"
import usePortalTexts from "../Vendor/texts/usePortalTexts"
import { PAYOUTS } from "../Vendor/texts/bundles"
import { useBilling, CARD_ENABLED } from "./BillingCard"
import { downloadPdf, pdfBytes } from "./pdf"
import { CardMark } from "../Storefront/checkout/PaymentLogos"
import { stripeErrorText } from "./stripeErrors"
import { viewerTexts } from "./ContractPdf"
import useSiteLanguage from "../Storefront/hooks/useSiteLanguage"

const STATUS = {
  succeeded: { label: "Paid", cls: "bg-admin_success/10 text-admin_success" },
  failed: { label: "Declined", cls: "bg-admin_error/10 text-admin_error" },
  pending: { label: "Processing", cls: "bg-admin_warning/20 text-admin_text" },
  // a bank debit on its way (SEPA: some business days)
  processing: { label: "Processing", cls: "bg-admin_warning/20 text-admin_text" },
  // a payment by hand: its invoice open on this page until its day
  awaiting: { label: "Awaiting payment", cls: "bg-admin_warning/20 text-admin_text" },
  expired: { label: "Expired", cls: "bg-admin_error/10 text-admin_error" },
}
// how a payment method reads: "Visa •••• 4242", "Bank account •••• 3000"
const methodName = (m, t) => (!m ? "" : m.type === "sepa_debit" ? t("Bank account •••• %s", m.last4 || "") : m.type === "bank_transfer" ? t`Bank transfer` : m.type === "manual" ? t`Manual payment` : `${BRANDS[m.brand] || m.brand || ""} •••• ${m.last4 || ""}`.trim())
const BRANDS = { visa: "Visa", mastercard: "Mastercard", amex: "Amex" }
/** A payment method as it reads, with its mark: the card's brand, or the bank. */
const MethodLabel = ({ method, t }) => (
  <span className="inline-flex items-center gap-2">
    {method?.type === "sepa_debit" ? <BuildingLibraryIcon className="w-5 h-5 text-admin_text_secondary" /> : <CardMark brand={method?.brand} />}
    {methodName(method, t)}
  </span>
)
const sumOf = (list) => (list || []).reduce((s, x) => s + (x.owed || 0), 0)
const currencyOf = (list) => list?.[0]?.currency || "eur"

function Figure({ label, value, sub, action, actionEnd = false, help, testId }) {
  return (
    <div className="rounded-lg border border-admin_bordered bg-admin_paper px-4 py-3 flex flex-col gap-1" data-testid={testId}>
      <div className="text-xs text-admin_text_secondary flex items-center gap-1">{label}{help}</div>
      <div className="text-xl font-semibold tabular-nums">{value}</div>
      {sub && <div className="text-xs text-admin_text_secondary">{sub}</div>}
      {action && <div className={`mt-2 flex ${actionEnd ? "justify-end" : ""}`}>{action}</div>}
    </div>
  )
}

const paymentsLink = () => {
  const q = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "")
  q.set("section", "company")
  q.set("company", "payments")
  // in the admin the company's sections are its Profile tab's (the vendor portal's are its `section`)
  if (typeof window !== "undefined" && /^\/([a-z]{2}\/)?admin(\/|$)/.test(window.location.pathname)) q.set("tab", "profile")
  q.delete("ptab")
  return `?${q.toString()}`
}

/**
 * Pay now, the way the shop's checkout pays: what is charged and to which card, asked first; then
 * the payment under way (a loader in the middle); then the payment confirmed — its invoice to
 * download — or what went wrong, with another try or the card to update.
 */
// `method`: what pays — a valid card, else the bank account (as the API tries them)
function PayNowDialog({ orgId, amount, currency, method, onClose, onOverview }) {
  const t = usePortalTexts(PAYOUTS)
  const [phase, setPhase] = useState("confirm") // confirm | paying | done | failed
  const [answer, setAnswer] = useState(null)
  const cardName = methodName(method, t)
  const charge = answer?.charges?.find((c) => c.status === "succeeded" || c.status === "processing") || answer?.charges?.[0] || null
  // a bank debit started: confirmed in some business days
  const processing = charge?.status === "processing"
  const paidWith = charge?.method ? methodName(charge.method, t) : cardName
  const pay = async () => {
    setPhase("paying")
    const res = await BillingPayNow(orgId).catch((e) => ({ error: e.message }))
    setAnswer(res)
    if (res?.overview) onOverview(res.overview)
    setPhase(res?.result ? "done" : "failed")
  }
  // nothing closes it while the card is being charged
  const close = phase === "paying" ? () => {} : onClose
  return (
    <Modal isOpen onClose={close} variant="admin" closeOnEscape={phase !== "paying"} allowCloseBtn={phase !== "paying"} title={phase === "confirm" ? t`Pay now` : ""} styleOverrides={{ container: "w-[min(94vw,480px)] bg-admin_paper rounded-lg border border-admin_bordered shadow-lg" }}>
      <div className="px-6 pb-6 pt-1 min-h-[320px] flex flex-col" data-testid="billing-pay-now-dialog" data-phase={phase}>
        {phase === "confirm" && (
          <div className="flex-1 flex flex-col gap-5">
            <p className="m-0 text-sm text-admin_text_secondary">{t`Aldoo's commission on your cash on delivery sales past their 30-day holding period: what the next settlement would charge, paid now.`}</p>
            <div className="rounded-lg border border-admin_bordered divide-y divide-admin_bordered">
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-sm text-admin_text_secondary">{t`Amount`}</span>
                <span className="text-xl font-semibold tabular-nums">{money(amount, currency)}</span>
              </div>
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-sm text-admin_text_secondary">{t`Payment method`}</span>
                <span className="text-sm font-medium"><MethodLabel method={method} t={t} /></span>
              </div>
            </div>
            <p className="m-0 text-sm">{t("Confirm a payment of %s with %s?", money(amount, currency), cardName)}</p>
            <div className="mt-auto flex justify-end gap-2">
              <Button variant="admin_secondary" onClick={onClose}>{t`Cancel`}</Button>
              <Button variant="admin_primary" onClick={pay} data-testid="billing-pay-now-confirm">{t("Pay %s", money(amount, currency))}</Button>
            </div>
          </div>
        )}
        {phase === "paying" && (
          // in the middle of the dialog, as the checkout waits on the card
          <div className="flex-1 flex flex-col items-center justify-center gap-5 text-center" data-testid="billing-pay-now-paying">
            <Spinner variant="admin_primary" size="xl" />
            <div className="text-lg font-semibold">{t`Processing the payment…`}</div>
            <p className="m-0 max-w-[340px] text-sm text-admin_text_secondary">{t`This takes a few seconds. Please keep this window open.`}</p>
          </div>
        )}
        {phase === "done" && (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center" data-testid="billing-pay-now-done">
            <CheckCircleIcon className={`w-20 h-20 ${processing ? "text-admin_warning" : "text-admin_success"}`} />
            <div className="text-2xl font-bold">{processing ? t`Payment started` : t`Thank you, payment received`}</div>
            <p className="m-0 max-w-[360px] text-sm text-admin_text_secondary">
              {processing
                ? t("%s is being debited from %s. A bank debit takes a few business days to confirm.", money(charge?.amount ?? amount, charge?.currency || currency), paidWith)
                : t("%s was paid with %s.", money(charge?.amount ?? amount, charge?.currency || currency), paidWith)}
            </p>
            {charge?.reference && <p className="m-0 text-xs text-admin_text_secondary">{t("Reference on your statement: %s", `ALDOO COMM ${charge.reference}`)}</p>}
            <div className="w-full max-w-[320px] flex flex-col gap-2 mt-2">
              <Button variant="admin_primary" className="w-full" onClick={onClose} data-testid="billing-pay-now-close">{t`Done`}</Button>
            </div>
          </div>
        )}
        {phase === "failed" && (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center" data-testid="billing-pay-now-failed">
            <ExclamationCircleIcon className="w-20 h-20 text-admin_error" />
            <div className="text-2xl font-bold">{t`The payment did not go through`}</div>
            <p className="m-0 max-w-[360px] text-sm text-admin_text_secondary">{answer?.code || answer?.error ? stripeErrorText(t, answer.code || answer.error, answer.message) : t`The card was declined`}</p>
            <div className="w-full max-w-[320px] flex flex-col gap-2 mt-2">
              <Button variant="admin_primary" className="w-full" onClick={pay} data-testid="billing-pay-now-retry">{t`Try again`}</Button>
              <a className="inline-flex justify-center" href={paymentsLink()}><Button variant="admin_secondary" className="w-full">{t`Update the card`}</Button></a>
              <Button variant="admin_text" className="w-full" onClick={onClose}>{t`Close`}</Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

/** The open payment's proforma, downloaded (made now by the API). */
async function downloadProforma(orgId, t) {
  const res = await BillingProformaFile(orgId).catch((e) => ({ error: e.message }))
  if (res?.pdf) return downloadPdf(res)
  showErrorToast(t`The proforma could not be made`)
}

// Stripe's form for the open payment: a card, a one-off bank debit (or Stripe's own bank transfer instructions)
function ManualPayForm({ amount, currency, onPaid, onStatus }) {
  const t = usePortalTexts(PAYOUTS)
  const stripe = useStripe()
  const elements = useElements()
  const [ready, setReady] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")
  const pay = async () => {
    if (!stripe || !elements) return
    setBusy(true)
    setError("")
    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({ elements, redirect: "if_required", confirmParams: { return_url: typeof window !== "undefined" ? window.location.href : undefined } })
    setBusy(false)
    if (stripeError) return setError(stripeError.type === "validation_error" ? stripeError.message : stripeErrorText(t, stripeError.decline_code || stripeError.code, stripeError.message))
    onPaid(paymentIntent?.status || "processing")
  }
  useEffect(() => onStatus?.({ busy, ready: ready && !!stripe, pay }), [busy, ready, stripe])
  return (
    <div className="flex flex-col gap-3" data-testid="billing-manual-form">
      <div className="relative min-h-[120px]">
        <PaymentElement options={{ layout: "tabs" }} onReady={() => setReady(true)} onLoadError={() => setReady(true)} />
        {(!ready || busy) && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-admin_paper/90 rounded-md">
            <Spinner variant="admin_primary" size="lg" />
            <span className="text-sm text-admin_text_secondary">{busy ? t`Processing the payment…` : t`Loading…`}</span>
          </div>
        )}
      </div>
      {error && <div className="rounded-md px-3 py-2 text-sm bg-admin_error/10 text-admin_error" data-testid="billing-manual-error">{error}</div>}
      <Button variant="admin_primary" className="w-full" onClick={pay} disabled={!ready || busy} data-testid="billing-manual-pay">{t("Pay %s", money(amount, currency))}</Button>
    </div>
  )
}

/**
 * A payment by hand of what is owed: the open one, or one opened by the API (the sales it covers are claimed, so
 * nothing charges them twice). Stripe's form (card or bank account), the bank transfer details with the
 * proforma's number as the reference, and the proforma itself. Paid by card: closed at once.
 */
function ManualPayDialog({ orgId, onClose, onOverview }) {
  const t = usePortalTexts(PAYOUTS)
  const language = useSiteLanguage()
  const [answer, setAnswer] = useState(null)
  const [phase, setPhase] = useState("loading") // loading | pay | done | processing | failed
  useEffect(() => {
    let gone = false
    BillingManual(orgId)
      .then((res) => {
        if (gone) return
        setAnswer(res)
        setPhase(res?.result ? (res.paid ? "done" : res.charge?.status === "processing" ? "processing" : "pay") : "failed")
      })
      .catch((e) => !gone && (setAnswer({ error: e.message }), setPhase("failed")))
    return () => {
      gone = true
    }
  }, [orgId])
  const paid = async (status) => {
    const res = await BillingManualCheck(orgId).catch(() => null)
    if (res?.overview) onOverview(res.overview)
    setPhase(res?.status === "paid" || status === "succeeded" ? "done" : "processing")
  }
  const c = answer?.charge
  const bank = answer?.bankTransfer
  return (
    <Modal isOpen onClose={onClose} variant="admin" closeOnEscape title={phase === "pay" ? t`Pay the commission` : ""} styleOverrides={{ container: "w-[min(94vw,560px)] max-h-[92vh] overflow-y-auto bg-admin_paper rounded-lg border border-admin_bordered shadow-lg" }}>
      <div className="px-6 pb-6 pt-1 min-h-[320px] flex flex-col gap-5" data-testid="billing-manual-dialog" data-phase={phase}>
        {phase === "loading" && (
          <div className="flex-1 flex flex-col items-center justify-center gap-4">
            <Spinner variant="admin_primary" size="xl" />
            <span className="text-sm text-admin_text_secondary">{t`Opening the payment…`}</span>
          </div>
        )}
        {phase === "pay" && c && (
          <>
            <div className="rounded-lg border border-admin_bordered divide-y divide-admin_bordered">
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-sm text-admin_text_secondary">{t`Amount`}</span>
                <span className="text-xl font-semibold tabular-nums">{money(c.amount, c.currency)}</span>
              </div>
              <div className="flex items-center justify-between px-4 py-3 text-sm">
                <span className="text-admin_text_secondary">{t("Proforma No. %s", c.proformaNumber || "")}</span>
                <span>{t("Payment due by %s", dateOnly(c.validUntil, language))}</span>
              </div>
              <div className="px-4 py-3">
                <Button variant="admin_secondary" className="w-full" onClick={() => downloadProforma(orgId, t)} data-testid="billing-proforma-download">{t`Download the proforma`}</Button>
              </div>
            </div>
            {answer.clientSecret && (
              <div className="flex flex-col gap-2">
                <div className="text-sm font-semibold">{t`Pay by card or bank account`}</div>
                <Elements stripe={getStripePromise()} options={{ clientSecret: answer.clientSecret, appearance: { theme: "stripe" }, locale: language === "bg" ? "bg" : "en" }}>
                  <ManualPayForm amount={c.amount} currency={c.currency} onPaid={paid} />
                </Elements>
              </div>
            )}
            {bank?.iban && (
              <div className="rounded-lg bg-admin_neutral_1 px-4 py-3 text-sm flex flex-col gap-1" data-testid="billing-bank-transfer">
                <div className="font-semibold mb-1">{t`Or by bank transfer`}</div>
                <div className="flex justify-between gap-3"><span className="text-admin_text_secondary">{t`Beneficiary`}</span><span className="text-right">{bank.holder || "Aldoo Ltd"}</span></div>
                <div className="flex justify-between gap-3"><span className="text-admin_text_secondary">IBAN</span><span className="font-mono text-right break-all">{bank.iban}</span></div>
                {bank.bic && <div className="flex justify-between gap-3"><span className="text-admin_text_secondary">BIC</span><span className="font-mono">{bank.bic}</span></div>}
                <div className="flex justify-between gap-3"><span className="text-admin_text_secondary">{t`Payment reference`}</span><span className="font-mono font-semibold">{c.proformaNumber}</span></div>
                <p className="m-0 mt-1 text-xs text-admin_text_secondary">{t`Quote the reference exactly: the transfer is matched to your proforma by it. It is confirmed within 1–2 business days.`}</p>
              </div>
            )}
          </>
        )}
        {(phase === "done" || phase === "processing") && (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center" data-testid="billing-manual-done">
            <CheckCircleIcon className={`w-20 h-20 ${phase === "processing" ? "text-admin_warning" : "text-admin_success"}`} />
            <div className="text-2xl font-bold">{phase === "processing" ? t`Payment started` : t`Thank you, payment received`}</div>
            {phase === "processing" && <p className="m-0 max-w-[380px] text-sm text-admin_text_secondary">{t`A bank debit or transfer is confirmed when it arrives, within 1–2 business days. The invoice is issued then.`}</p>}
            <Button variant="admin_primary" className="w-full max-w-[320px]" onClick={onClose} data-testid="billing-manual-close">{t`Done`}</Button>
          </div>
        )}
        {phase === "failed" && (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center">
            <ExclamationCircleIcon className="w-20 h-20 text-admin_error" />
            <div className="text-xl font-bold">{answer?.error === "nothing_owed" ? t`Nothing is owed now` : t`The payment form could not be opened`}</div>
            <Button variant="admin_secondary" className="w-full max-w-[320px]" onClick={onClose}>{t`Close`}</Button>
          </div>
        )}
      </div>
    </Modal>
  )
}

export function CommissionTab({ orgId, canEdit, locale }) {
  const t = usePortalTexts(PAYOUTS)
  const { overview, reload, setOverview } = useBilling(orgId)
  const [paying, setPaying] = useState(false)
  // the payment by hand (its dialog)
  const [manualOpen, setManualOpen] = useState(false)
  // the document open in the viewer: { invoice, which } — "stripe" the invoice, else its report
  const [viewing, setViewing] = useState(null)
  if (!overview) return null
  const docOf = new Map((overview.invoices || []).map((inv) => [String(inv._id), inv]))
  const owedNow = sumOf(overview.owedNow)
  const currency = currencyOf(overview.owedNow)
  const card = overview.card
  const bankAccount = overview.bank
  // what pays now: a valid card, else the bank account (as the API tries them)
  const payingMethod = card?.valid ? { type: "card", ...card } : bankAccount ? { type: "sepa_debit", ...bankAccount } : card ? { type: "card", ...card } : null
  const payNow = () => setPaying({ amount: owedNow, currency })
  const holdText = overview.hold?.reason === "manual_overdue" ? t`Cash on delivery is on hold: the commission was not paid in time.` : overview.hold?.reason === "charge_failed" ? t`Cash on delivery is on hold: the commission could not be charged.` : null
  const open = overview.manual
  return (
    <div className="flex flex-col gap-4" data-testid="billing-commission">
      {viewing && <BillingInvoiceViewer orgId={orgId} invoice={viewing.invoice} which={viewing.which} onClose={() => setViewing(null)} />}
      {/* the amount asked for when it opened: the figures behind it update once it is paid */}
      {paying && <PayNowDialog orgId={orgId} amount={paying.amount} currency={paying.currency} method={payingMethod} onOverview={setOverview} onClose={() => (setPaying(false), reload())} />}
      {manualOpen && <ManualPayDialog orgId={orgId} onOverview={setOverview} onClose={() => (setManualOpen(false), reload())} />}
      {holdText && <div className="rounded-lg px-4 py-3 text-sm bg-admin_error/10 text-admin_error">{holdText} <a className="underline font-medium" href={paymentsLink()}>{t`Update the payment method`}</a></div>}
      {!card?.valid && !bankAccount && <div className="rounded-lg px-4 py-3 text-sm bg-admin_neutral_1 text-admin_text" data-testid="billing-card-missing">{t`No card or bank debit for the commission: it is paid by hand here, from a proforma. Cash on delivery stays on.`}{CARD_ENABLED && <> <a className="underline font-medium" href={paymentsLink()}>{t`Add a card`}</a></>}</div>}
      {/* a payment by hand waiting: what, by when, the proforma */}
      {open && (
        <div className="rounded-lg border border-admin_warning/50 bg-admin_warning/10 px-4 py-3 flex flex-wrap items-center gap-3" data-testid="billing-manual-open">
          <span className="text-sm flex-1 min-w-[220px]">{open.status === "processing" ? t("%s: your payment is on its way.", money(open.amount, open.currency)) : t("%s is waiting to be paid by %s", money(open.amount, open.currency), dateOnly(open.validUntil, locale))} <span className="text-admin_text_secondary">{t("Proforma No. %s", open.proformaNumber || "")}</span></span>
          <Button variant="admin_secondary" size="sm" onClick={() => downloadProforma(orgId, t)}>{t`Proforma invoice`}</Button>
          {canEdit && open.status === "awaiting" && <Button variant="admin_primary" size="sm" onClick={() => setManualOpen(true)} data-testid="billing-manual-resume">{t`Pay`}</Button>}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* what is certain: the sales past their 30 days — charged at the settlement, or paid early */}
        <Figure
          label={t`Due now`}
          value={money(owedNow, currency)}
          sub={t("the sales past their 30-day holding period — due on %s, or pay now", dateOnly(overview.nextSettlementDate, locale))}
          testId="billing-owed-now"
          actionEnd
          help={<HelpTip label={t`How the commission is paid`} text={t`With cash on delivery the courier pays you the whole amount, so Aldoo's commission on those orders is yours to pay. It is taken from your online payouts first; what they do not cover is charged to your card on the settlement day, for the sales past their holding period. You can also pay early.`} />}
          action={
            canEdit && owedNow > 0 && !open ? (
              <div className="flex flex-wrap justify-end gap-2">
                {/* by hand: card, bank account, or a bank transfer from its proforma */}
                <Button variant={payingMethod ? "admin_secondary" : "admin_primary"} onClick={() => setManualOpen(true)} data-testid="billing-manual">
                  {t`Pay by hand`}
                </Button>
                {payingMethod && (
                  <Button variant="admin_primary" onClick={payNow} disabled={!!paying} data-testid="billing-pay-now">
                    {t`Pay now`}
                  </Button>
                )}
              </div>
            ) : null
          }
        />
        {/* what pays: the card first, the bank account when there is no valid card (or it is declined) */}
        <Figure
          label={t`Payment method`}
          value={payingMethod ? <span className="text-base"><MethodLabel method={payingMethod} t={t} /></span> : "—"}
          sub={card && bankAccount ? (card.valid ? t("then %s if the card fails", methodName({ type: "sepa_debit", ...bankAccount }, t)) : t`the card expired: the bank account is debited`) : card ? (card.valid ? t("valid to %s/%s", String(card.expMonth).padStart(2, "0"), card.expYear) : t`expired`) : bankAccount ? t`SEPA Direct Debit` : t`none yet`}
          testId="billing-card-summary"
          action={canEdit ? <a className="text-xs text-admin_primary underline" href={paymentsLink()}>{payingMethod ? t`Change the payment method` : CARD_ENABLED ? t`Add a card` : t`Allow the debits`}</a> : null}
        />
      </div>
      <Section title={t`Charges`} subtitle={t`What was charged to your card or debited from your bank account for the commission; the reference is on your statement as ALDOO COMM …`}>
        {overview.charges?.length ? (
          <table className="w-full text-sm" data-testid="billing-charges">
            <thead>
              <tr className="text-left text-xs text-admin_text_secondary border-b border-admin_bordered">
                <th className="py-2 pr-3 font-medium">{t`Date`}</th>
                <th className="py-2 pr-3 font-medium">{t`Purpose`}</th>
                <th className="py-2 pr-3 font-medium">{t`Reference`}</th>
                <th className="py-2 pr-3 font-medium">{t`Payment method`}</th>
                <th className="py-2 pr-3 font-medium text-right">{t`Amount`}</th>
                <th className="py-2 pr-3 font-medium">{t`Status`}</th>
                <th className="py-2 pr-3 font-medium">{t`Invoice No.`}</th>
                <th className="py-2 font-medium">{t`Report`}</th>
              </tr>
            </thead>
            <tbody>
              {overview.charges.map((c) => {
                const s = STATUS[c.status] || STATUS.pending
                const doc = docOf.get(String(c.invoiceId || ""))
                return (
                  <tr key={c._id} className="border-b border-admin_bordered last:border-b-0">
                    <td className="py-2 pr-3 whitespace-nowrap">{dateOnly(c.paidAt || c.createdAt, locale)}</td>
                    <td className="py-2 pr-3">{c.kind === "early" ? t`Early payment` : c.kind === "manual" ? t`Manual payment` : t`Monthly settlement`}</td>
                    <td className="py-2 pr-3 whitespace-nowrap font-mono text-xs" data-testid="billing-charge-reference">{c.reference || "—"}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{c.method ? <MethodLabel method={c.method} t={t} /> : "—"}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{money(c.amount, c.currency)}</td>
                    <td className="py-2 pr-3">
                      <Chip className={s.cls}>{t(s.label)}</Chip>
                      {/* why it was declined, in the vendor's language */}
                      {c.status === "failed" && (c.lastErrorCode || c.lastError) && <div className="text-xs text-admin_error mt-1 max-w-[260px] whitespace-normal" data-testid="billing-charge-reason">{stripeErrorText(t, c.lastErrorCode, c.lastError)}</div>}
                      {c.status === "awaiting" && c.validUntil && <div className="text-xs text-admin_text_secondary mt-1">{t("Payment due by %s", dateOnly(c.validUntil, locale))}</div>}
                      {c.status === "failed" && <div className="text-xs text-admin_text_secondary mt-1">{c.nextRetryAt ? t("Next try %s", dateOnly(c.nextRetryAt, locale)) : t`Retries ended: update the payment method or pay now`}</div>}
                    </td>
                    {/* the payment's invoice (Stripe's) and its report (the orders behind it), each in the viewer */}
                    <td className="py-2 pr-3 whitespace-nowrap">
                      {doc?.stripe?.number ? (
                        <button type="button" onClick={() => setViewing({ invoice: doc, which: "stripe" })} className="font-mono font-medium text-admin_primary hover:underline" data-testid="billing-invoice-view">{doc.stripe.number}</button>
                      ) : c.status === "succeeded" ? (
                        <span className="text-admin_text_secondary">{t`Being issued`}</span>
                      ) : (
                        <span className="text-admin_text_secondary">—</span>
                      )}
                    </td>
                    <td className="py-2 whitespace-nowrap">
                      {doc ? (
                        <button type="button" onClick={() => setViewing({ invoice: doc, which: null })} className="font-mono text-admin_primary hover:underline" data-testid="billing-statement-view">{doc.number}</button>
                      ) : (
                        <span className="text-admin_text_secondary">—</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        ) : (
          <Empty>{t`No charges yet`}</Empty>
        )}
      </Section>
    </div>
  )
}

/**
 * A commission document in the framework's PDF viewer, in a modal — downloadable from its toolbar:
 * `which` "stripe" the invoice Stripe issued, else Aldoo's statement of the orders behind it.
 */
export function BillingInvoiceViewer({ orgId, invoice, which = null, onClose }) {
  const t = usePortalTexts(PAYOUTS)
  const [answer, setAnswer] = useState(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    let gone = false
    BillingInvoiceFile(orgId, invoice._id, which)
      .then((res) => !gone && (res?.pdf ? setAnswer(res) : setFailed(true)))
      .catch(() => !gone && setFailed(true))
    return () => {
      gone = true
    }
  }, [orgId, invoice._id, which])
  const bytes = useMemo(() => pdfBytes(answer), [answer])
  const texts = useMemo(() => ({ ...viewerTexts(t), error: t`The invoice could not be opened` }), [t])
  const pdfModal = usePdfModal()
  return (
    <Modal
      isOpen
      onClose={onClose}
      title={which === "stripe" ? t("Invoice No. %s", invoice.stripe?.number || "") : t("Report No. %s", invoice.number)}
      variant={pdfModal.variant}
      closeOnEscape
      closeOnClickOutside
      styleOverrides={pdfModal.styleOverrides}
    >
      {failed ? (
        <div className="m-4 rounded-md border border-admin_bordered bg-admin_neutral_1 p-6 text-sm text-admin_text_secondary">{t`The invoice could not be opened`}</div>
      ) : (
        <div className="w-full" data-testid="billing-invoice-viewer">
          <PDFViewer src={bytes} height={pdfModal.height} onDownload={answer ? () => downloadPdf(answer) : undefined} texts={texts} />
        </div>
      )}
    </Modal>
  )
}
